import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Box, Grid, Typography, Paper, Avatar, Divider, Stack, Chip, Skeleton, Card, CardContent, Button, Alert } from '@mui/material';
import {
    Email,
    Phone,
    LocationOn,
    Storefront,
    AccountBalanceWallet,
    AccountBalance,
    Inventory,
    Star,
    Badge,
    Settings,
    OpenInNew
} from '@mui/icons-material';
import { Loader } from '@googlemaps/js-api-loader';
import { useCurrentVendor } from 'hooks/useUser';
import BankAccountManager from 'views/common/bank-accounts/BankAccountManager';

const VendorProfile = () => {
    const navigate = useNavigate();
    const { data, isLoading, error } = useCurrentVendor();
    const mapRef = useRef(null);

    useEffect(() => {
        if (!data?.vendor?.location?.coordinates || !mapRef.current) return;
        const [lng, lat] = data.vendor.location.coordinates;
        const vendorName = data.vendor.name;

        const loader = new Loader({
            apiKey: 'AIzaSyBUQgsNW1o_DyxClf4ilZiDUoymPEz59Fs',
            libraries: ['places']
        });

        loader.load().then(() => {
            if (!mapRef.current) return;
            const position = { lat, lng };
            const map = new window.google.maps.Map(mapRef.current, {
                center: position,
                zoom: 15,
                disableDefaultUI: true,
                zoomControl: true
            });
            new window.google.maps.Marker({ position, map, title: vendorName });
        });
    }, [data?.vendor?.location?.coordinates, data?.vendor?.name]);

    if (isLoading) return <ProfileSkeleton />;
    if (error || !data)
        return (
            <Box p={4}>
                <Alert severity="error">Unable to load vendor data.</Alert>
            </Box>
        );

    const { vendor, user, wallet } = data;

    return (
        <Box sx={{ pb: 5 }}>
            {/* Header Section */}
            <Paper sx={{ borderRadius: 4, overflow: 'hidden', mb: 3, position: 'relative', border: '1px solid #eee', boxShadow: 'none' }}>
                <Box sx={{ height: 200, width: '100%', bgcolor: 'grey.200' }}>
                    <img src={vendor.banner} alt="Banner" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </Box>
                <Box
                    sx={{
                        p: 3,
                        mt: -6,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-end',
                        flexWrap: 'wrap',
                        gap: 2
                    }}
                >
                    <Stack direction="row" spacing={3} alignItems="flex-end">
                        <Avatar
                            src={vendor.logo}
                            sx={{ width: 110, height: 110, border: '4px solid #fff', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                        />
                        <Box sx={{ pb: 1 }}>
                            <Typography variant="h4" fontWeight={800}>
                                {vendor.name}
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Chip
                                    label={vendor.isAvailable ? 'Active' : 'Offline'}
                                    color={vendor.isAvailable ? 'success' : 'default'}
                                    size="small"
                                />
                                <Typography variant="body2" color="text.secondary" display="flex" alignItems="center">
                                    <Star sx={{ fontSize: 16, mr: 0.5, color: '#faaf00' }} /> {vendor.ratings} Vendor Rating
                                </Typography>
                            </Stack>
                        </Box>
                    </Stack>

                    {/* Navigation to Settings */}
                    <Button
                        variant="contained"
                        startIcon={<Settings />}
                        endIcon={<OpenInNew />}
                        onClick={() => navigate('/vendor/settings')}
                        sx={{ mb: 1, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                    >
                        Account Settings
                    </Button>
                </Box>
            </Paper>

            <Grid container spacing={3}>
                {/* Statistics Overview */}
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Available Balance"
                        value={`₦${wallet.availableBalance?.toLocaleString()}`}
                        icon={<AccountBalanceWallet />}
                        color="success"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Pending Payouts"
                        value={`₦${wallet.pendingBalance?.toLocaleString()}`}
                        icon={<AccountBalanceWallet />}
                        color="warning"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard title="Total Products" value={vendor.products?.length || 0} icon={<Inventory />} color="primary" />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard title="Marketplace" value={vendor.marketCategory?.name} icon={<Storefront />} color="secondary" />
                </Grid>

                {/* Content Area */}
                <Grid item xs={12} md={7}>
                    <Stack spacing={3}>
                        <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #eee', boxShadow: 'none' }}>
                            <Typography variant="h6" fontWeight={700} gutterBottom>
                                Business Profile
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Grid container spacing={3}>
                                <InfoItem icon={<Email />} label="Public Email" value={vendor.email} />
                                <InfoItem icon={<Phone />} label="Contact Number" value={vendor.phoneNumber} />
                                <InfoItem icon={<LocationOn />} label="Business Address" value={vendor.address} />
                                <InfoItem icon={<Badge />} label="Business ID" value={vendor._id?.slice(-10).toUpperCase()} />
                            </Grid>
                        </Paper>

                        <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #eee', boxShadow: 'none' }}>
                            <Typography variant="h6" fontWeight={700} gutterBottom>
                                Store Location
                            </Typography>
                            <Box ref={mapRef} sx={{ width: '100%', height: 320, borderRadius: 2, bgcolor: 'grey.100' }} />
                        </Paper>
                    </Stack>
                </Grid>

                {/* Side Info */}
                <Grid item xs={12} md={5}>
                    <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #eee', boxShadow: 'none' }}>
                        <Typography variant="h6" fontWeight={700} gutterBottom>
                            Account Owner
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Stack spacing={3}>
                            <Box display="flex" alignItems="center" gap={2}>
                                <Avatar sx={{ width: 50, height: 50, bgcolor: 'primary.light' }}>{user.firstName[0]}</Avatar>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight={700}>
                                        {user.firstName} {user.lastName}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {user.role} Account
                                    </Typography>
                                </Box>
                            </Box>
                            <Divider />
                            <InfoRow
                                label="KYC Status"
                                value={
                                    <Stack alignItems="flex-end" spacing={1}>
                                        <Chip
                                            label={user.kycStatus || 'Not Submitted'}
                                            size="small"
                                            color={user.kycStatus === 'verified' ? 'success' : 'warning'}
                                            variant="outlined"
                                            sx={{ fontWeight: 700, textTransform: 'capitalize' }}
                                        />
                                        {user.kycStatus !== 'verified' && (
                                            <Button
                                                variant="text"
                                                size="small"
                                                color="primary"
                                                onClick={() => navigate('/vendor/kyc')}
                                                sx={{ p: 0, minWidth: 0, textTransform: 'none', fontWeight: 700, fontSize: '0.75rem' }}
                                            >
                                                Complete KYC Now →
                                            </Button>
                                        )}
                                    </Stack>
                                }
                            />
                            <InfoRow label="Login Email" value={user.email} />
                            <InfoRow label="Linked Phone" value={user.phoneNumber} />
                        </Stack>
                    </Paper>
                </Grid>
            </Grid>

            {/* Bank Accounts Section */}
            <Box mt={3}>
                <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #eee', boxShadow: 'none' }}>
                    <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                        <AccountBalance color="primary" />
                        <Typography variant="h6" fontWeight={700}>
                            My Bank Accounts
                        </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" mb={3}>
                        Manage your saved bank accounts. These will appear as options when you request a payout.
                    </Typography>
                    <BankAccountManager />
                </Paper>
            </Box>
        </Box>
    );
};

// Internal Components
const StatCard = ({ title, value, icon, color }) => (
    <Card sx={{ height: '100%', borderRadius: 3, border: '1px solid #eee', boxShadow: 'none' }}>
        <CardContent>
            <Stack direction="row" spacing={2} alignItems="center">
                <Avatar sx={{ bgcolor: `${color}.main`, color: '#fff', borderRadius: 2 }}>{icon}</Avatar>
                <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {title}
                    </Typography>
                    <Typography variant="h6" fontWeight={800}>
                        {value}
                    </Typography>
                </Box>
            </Stack>
        </CardContent>
    </Card>
);

StatCard.propTypes = {
    title: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    icon: PropTypes.node.isRequired,
    color: PropTypes.string.isRequired
};

const InfoItem = ({ icon, label, value }) => (
    <Grid item xs={12} sm={6}>
        <Stack direction="row" spacing={1.5}>
            <Box sx={{ color: 'primary.main', mt: 0.5 }}>{icon}</Box>
            <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    {label}
                </Typography>
                <Typography variant="body2" fontWeight={500} sx={{ wordBreak: 'break-word' }}>
                    {value}
                </Typography>
            </Box>
        </Stack>
    </Grid>
);

InfoItem.propTypes = {
    icon: PropTypes.node.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.string
};

const InfoRow = ({ label, value }) => (
    <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.secondary">
            {label}
        </Typography>
        <Typography variant="body2" fontWeight={600}>
            {value}
        </Typography>
    </Box>
);

InfoRow.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired
};

const ProfileSkeleton = () => (
    <Box p={4}>
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 4, mb: 3 }} />
        <Grid container spacing={2} mb={3}>
            {[1, 2, 3, 4].map((i) => (
                <Grid item xs={12} sm={3} key={i}>
                    <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 3 }} />
                </Grid>
            ))}
        </Grid>
        <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
                <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 3 }} />
            </Grid>
            <Grid item xs={12} md={5}>
                <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 3 }} />
            </Grid>
        </Grid>
    </Box>
);

export default VendorProfile;
